import { useState, useEffect } from 'react';
import { FirebaseError } from 'firebase/app';
import {
  getAuth,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  signOut as fbSignOut,
  onAuthStateChanged,
  updateProfile,
  type User,
} from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { doc, onSnapshot, collection, query, where, limit, getDocs } from 'firebase/firestore';
import { db } from './firebase';
import {
  getPayoutEligibility,
  getUnpaidBalance,
  type PayoutEligibility,
} from './partnerStatsFilters';

const auth = getAuth();
const functions = getFunctions();

export const SUPPORT_EMAIL = 'support@luvlustapp.com';

export function mapAuthError(err: unknown, fallback: string): string {
  if (err instanceof FirebaseError) {
    switch (err.code) {
      case 'auth/invalid-credential':
      case 'auth/wrong-password':
      case 'auth/user-not-found':
      case 'auth/invalid-email':
        return 'Incorrect email or password. Please try again.';
      case 'auth/too-many-requests':
        return 'Too many attempts. Please wait a few minutes and try again.';
      case 'auth/email-already-in-use':
        return 'An account with this email already exists. Try signing in.';
      case 'auth/weak-password':
        return 'Password must be at least 6 characters.';
      default:
        break;
    }
  }
  return err instanceof Error ? err.message : fallback;
}

export async function sendPasswordReset(email: string) {
  return sendPasswordResetEmail(auth, email);
}

export async function signUp(email: string, password: string, displayName: string) {
  const cred = await createUserWithEmailAndPassword(auth, email, password);
  await updateProfile(cred.user, { displayName });
  const initPartner = httpsCallable(functions, 'initPartner');
  const result = await initPartner({ displayName });
  return { user: cred.user, partner: result.data };
}

export async function signIn(email: string, password: string) {
  return signInWithEmailAndPassword(auth, email, password);
}

export async function updatePartnerDisplayName(displayName: string) {
  const fn = httpsCallable<{ displayName: string }, { ok: boolean; displayName: string }>(
    functions,
    'updatePartnerDisplayName',
  );
  const result = await fn({ displayName });
  return result.data;
}

export async function signOut() {
  return fbSignOut(auth);
}

export function useAuthState() {
  const [user, setUser] = useState<User | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    return onAuthStateChanged(auth, async (u) => {
      setUser(u);
      if (u) {
        const tokenResult = await u.getIdTokenResult();
        setIsAdmin(!!tokenResult.claims.admin);
      } else {
        setIsAdmin(false);
      }
      setLoading(false);
    });
  }, []);

  return { user, isAdmin, loading };
}

export interface PartnerData {
  uid: string;
  email: string;
  displayName: string;
  referralCode: string;
  commissionPercent: number | null;
  status: string;
  totalEarnings: number;
  totalPaid: number;
  /** Legacy flag; campaigns are available to all partners. */
  campaignAdmin?: boolean;
}

export function usePartnerData(partnerId: string | null) {
  const [data, setData] = useState<PartnerData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!partnerId) {
      setLoading(false);
      return;
    }
    return onSnapshot(doc(db, 'partners', partnerId), (snap) => {
      setData(snap.exists() ? (snap.data() as PartnerData) : null);
      setLoading(false);
    });
  }, [partnerId]);

  return { data, loading };
}

export interface PartnerBalance {
  totalEarnings: number;
  totalPaid: number;
  unpaid: number;
  eligibility: PayoutEligibility;
}

export function usePartnerBalance() {
  const { user, loading: authLoading } = useAuthState();
  const [balance, setBalance] = useState<PartnerBalance | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      setBalance(null);
      setLoading(false);
      return;
    }

    let cancelled = false;
    let unsubscribe: (() => void) | undefined;

    async function load() {
      setLoading(true);
      const snap = await getDocs(
        query(collection(db, 'partners'), where('uid', '==', user!.uid), limit(1)),
      );
      if (cancelled) return;
      if (snap.empty) {
        setBalance(null);
        setLoading(false);
        return;
      }

      const partnerId = snap.docs[0].id;
      unsubscribe = onSnapshot(doc(db, 'partners', partnerId), (partnerSnap) => {
        if (cancelled) return;
        if (!partnerSnap.exists()) {
          setBalance(null);
          setLoading(false);
          return;
        }
        const data = partnerSnap.data() as PartnerData;
        const unpaid = getUnpaidBalance(data.totalEarnings ?? 0, data.totalPaid ?? 0);
        setBalance({
          totalEarnings: data.totalEarnings ?? 0,
          totalPaid: data.totalPaid ?? 0,
          unpaid,
          eligibility: getPayoutEligibility(unpaid),
        });
        setLoading(false);
      });
    }

    void load();

    return () => {
      cancelled = true;
      unsubscribe?.();
    };
  }, [user, authLoading]);

  return { balance, loading: authLoading || loading };
}
